import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

const OPENER_POS_KEY = 'sense.admin.sidebarOpenerPos';
const DRAG_THRESHOLD_PX = 6;

interface OpenerPos {
  x: number;
  y: number;
}

interface AdminSidebarOpenerProps {
  onOpen: () => void;
}

function loadOpenerPos(): OpenerPos | null {
  try {
    const raw = localStorage.getItem(OPENER_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OpenerPos;
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return parsed;
    }
  } catch {
    // ignore storage errors
  }
  return null;
}

function saveOpenerPos(pos: OpenerPos) {
  try {
    localStorage.setItem(OPENER_POS_KEY, JSON.stringify(pos));
  } catch {
    // ignore storage errors
  }
}

function clampPos(x: number, y: number, width: number, height: number): OpenerPos {
  const maxX = Math.max(0, window.innerWidth - width);
  const maxY = Math.max(0, window.innerHeight - height);
  return {
    x: Math.min(Math.max(0, x), maxX),
    y: Math.min(Math.max(0, y), maxY),
  };
}

export function AdminSidebarOpener({ onOpen }: AdminSidebarOpenerProps): JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<OpenerPos | null>(() => loadOpenerPos());
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    originX: number;
    originY: number;
    width: number;
    height: number;
    moved: boolean;
  } | null>(null);

  const keepInViewport = useCallback(() => {
    setPos((current) => {
      if (!current || !buttonRef.current) return current;
      const { width, height } = buttonRef.current.getBoundingClientRect();
      const next = clampPos(current.x, current.y, width, height);
      if (next.x === current.x && next.y === current.y) return current;
      saveOpenerPos(next);
      return next;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('resize', keepInViewport);
    return () => window.removeEventListener('resize', keepInViewport);
  }, [keepInViewport]);

  function handlePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const dx = event.clientX - drag.startClientX;
    const dy = event.clientY - drag.startClientY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

    drag.moved = true;
    setDragging(true);
    const next = clampPos(drag.originX + dx, drag.originY + dy, drag.width, drag.height);
    setPos(next);
  }

  function endPointer(event: ReactPointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const wasDrag = drag.moved;
    dragRef.current = null;
    setDragging(false);

    if (wasDrag) {
      setPos((current) => {
        if (current) saveOpenerPos(current);
        return current;
      });
      return;
    }

    onOpen();
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`admin-sidebar-opener${dragging ? ' admin-sidebar-opener--dragging' : ''}`}
      aria-label="Show sidebar"
      style={
        pos
          ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
          : undefined
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
    >
      <img src="/just-logo-sense-ai.png" alt="Sense AI" draggable={false} />
    </button>
  );
}
